import { useEffect, useMemo, useState } from "react";
import { ReplaySource, type Recording } from "@radar-sim/engine";
import { getAreaData } from "@radar-sim/area-data";
import type { Scenario } from "@radar-sim/schema";
import { GarminGPSMAP } from "../chrome/GarminGPSMAP.js";
import { PPI } from "../ppi/PPI.js";
import { DEFAULT_DISPLAY_STATE, type DisplayState } from "../ppi/displayState.js";

export interface DebriefProps {
  recording: Recording;
  scenarios: ReadonlyArray<Scenario>;
  onLeave: () => void;
}

/**
 * Replay-mode debrief. Builds a `ReplaySource` over a stored `Recording`
 * and mounts the same chrome + PPI the live session used. Both the student
 * and instructor watch the identical playback — that's the point of the
 * one-interface-three-implementations spine.
 *
 * Scrubber is a basic seek-by-index. Phase 5 polish lifts this into a
 * proper timeline with annotated events.
 */
export function Debrief(props: DebriefProps): JSX.Element {
  const [display, setDisplay] = useState<DisplayState>(DEFAULT_DISPLAY_STATE);
  const [paused, setPaused] = useState(false);
  const [cursor, setCursor] = useState(0);

  const source = useMemo(
    () => new ReplaySource(props.recording, { autoTick: true, rate: 1 }),
    [props.recording],
  );

  useEffect(() => {
    void source.connect();
    return () => {
      void source.disconnect();
    };
  }, [source]);

  // Track the cursor for the scrubber. ReplaySource exposes `cursorIndex`
  // but doesn't notify; we poll cheaply at 4 Hz.
  useEffect(() => {
    const handle = setInterval(() => setCursor(source.cursorIndex), 250);
    return () => clearInterval(handle);
  }, [source]);

  const scenario = props.scenarios.find(
    (s) => s.metadata.id === props.recording.metadata.scenarioId,
  );
  const area =
    scenario !== undefined ? getAreaData(scenario.metadata.area) : undefined;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1280,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 13,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            background: "rgba(245,158,11,0.15)",
            color: "var(--warn)",
          }}
        >
          Debrief
        </span>
        <span style={{ color: "var(--text-muted)" }}>
          recording{" "}
          <code style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
            {props.recording.metadata.id.slice(0, 8)}…
          </code>
        </span>
        <span style={{ color: "var(--text-muted)" }}>
          {props.recording.snapshots.length} snapshots @{" "}
          {props.recording.metadata.tickRateHz} Hz
        </span>
        <button
          onClick={props.onLeave}
          style={{
            marginLeft: "auto",
            background: "transparent",
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            borderRadius: "var(--radius-sm)",
            padding: "5px 12px",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Close debrief
        </button>
      </div>
      <GarminGPSMAP
        ppi={
          <PPI
            source={source}
            area={area}
            scenarioId={props.recording.metadata.scenarioId}
            display={display}
            onDisplayChange={setDisplay}
          />
        }
        display={display}
        onDisplayChange={setDisplay}
        scenarioName={
          scenario?.metadata.name ?? props.recording.metadata.scenarioId
        }
        paused={paused}
        onPause={async () => {
          setPaused(true);
          await source.sendCommand({ kind: "pause" });
        }}
        onResume={async () => {
          setPaused(false);
          await source.sendCommand({ kind: "resume" });
        }}
      />
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          padding: "10px 14px",
          color: "var(--text)",
          fontSize: 12,
        }}
      >
        <input
          type="range"
          min={0}
          max={Math.max(0, props.recording.snapshots.length - 1)}
          value={cursor}
          onChange={(e) => {
            const i = parseInt(e.target.value, 10);
            source.seek(i);
            setCursor(i);
          }}
          aria-label="Recording scrubber"
          style={{ flex: 1, accentColor: "var(--accent)" }}
        />
        <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>
          <strong style={{ color: "var(--text)" }}>{cursor + 1}</strong>
          {" / "}
          {props.recording.snapshots.length}
        </span>
      </div>
    </div>
  );
}
